import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useMicLevel } from './useMicLevel.js';
import { levelToZone, ZONE_META, type NoiseZone } from './noiseZones.js';
import {
  getNoiseSensitivity,
  setNoiseSensitivity,
  MIN_SENSITIVITY,
  MAX_SENSITIVITY,
} from './noiseSettings.js';
import styles from './NoiseMeterOverlay.module.css';

export interface NoiseMeterOverlayProps {
  onClose: () => void;
}

/**
 * Full-screen classroom noise meter. The whole scene washes from calm (cool,
 * gentle) to intense (red, darker) as the room gets louder, with a central
 * meter the kids can read. Driven by the mic via `useMicLevel`; the per-frame
 * level is written to a CSS `--level` custom property (0..1) so all the motion
 * is pure CSS — no React re-render each frame. The zone word updates only when
 * the band changes.
 *
 * Mounted only while open (the button conditionally renders it), so every open
 * is a clean slate — calm streak + best reset, mic re-acquired, one interval.
 * Closes on Esc or the Close button; unmounting stops the mic stream.
 */
export function NoiseMeterOverlay({ onClose }: NoiseMeterOverlayProps) {
  const rootRef = useRef<HTMLDivElement | null>(null);
  const zoneRef = useRef<NoiseZone>('calm');
  const [zone, setZone] = useState<NoiseZone>('calm');
  const [calibrating, setCalibrating] = useState(false);
  const [sensitivity, setSensitivity] = useState(() => getNoiseSensitivity());
  const sensitivityRef = useRef(sensitivity);
  sensitivityRef.current = sensitivity;

  const updateSensitivity = (value: number) => {
    setSensitivity(value);
    sensitivityRef.current = value;
    setNoiseSensitivity(value);
  };

  // Calm streak: how long the class has stayed calm/good (not loud). Counts UP
  // while quiet, and RESETS to 0 the moment it goes loud — so the class roots
  // for each other to keep it going. Milestones escalate the celebration
  // (growing stars + praise), and the best streak is tracked to beat.
  const [streakOn, setStreakOn] = useState(false);
  const [streakMs, setStreakMs] = useState(0);
  const [bestMs, setBestMs] = useState(0);
  const [stars, setStars] = useState(0);
  const [toast, setToast] = useState<string | null>(null);
  const [broke, setBroke] = useState(false);
  const streakRef = useRef(0);
  const bestRef = useRef(0);
  const starsRef = useRef(0);
  const streakOnRef = useRef(false);
  streakOnRef.current = streakOn;
  const toastTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const brokeTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  const resetStreak = () => {
    streakRef.current = 0;
    starsRef.current = 0;
    bestRef.current = 0;
    setStreakMs(0);
    setStars(0);
    setBestMs(0);
    setBroke(false);
    setToast(null);
  };
  const startStreak = () => {
    resetStreak();
    setStreakOn(true);
  };
  const stopStreak = () => setStreakOn(false);

  // ONE interval for the lifetime of the (freshly mounted) overlay. It reads
  // streakOn + zone from refs and ticks off wall-clock deltas. Because the
  // overlay mounts fresh each open, the streak + best start at zero every time.
  useEffect(() => {
    let last = Date.now();
    const id = setInterval(() => {
      const now = Date.now();
      const dt = now - last;
      last = now;
      if (!streakOnRef.current) return;
      if (zoneRef.current === 'loud') {
        if (streakRef.current > 2000) {
          bestRef.current = Math.max(bestRef.current, streakRef.current);
          setBestMs(bestRef.current);
          setBroke(true);
          clearTimeout(brokeTimerRef.current);
          brokeTimerRef.current = setTimeout(() => setBroke(false), 2000);
        }
        if (streakRef.current !== 0) {
          streakRef.current = 0;
          starsRef.current = 0;
          setStreakMs(0);
          setStars(0);
        }
        return;
      }
      streakRef.current += dt;
      setStreakMs(streakRef.current);
      if (streakRef.current > bestRef.current) {
        bestRef.current = streakRef.current;
        setBestMs(bestRef.current);
      }
      const passed = STREAK_MILESTONES.filter((s) => streakRef.current >= s * 1000).length;
      if (passed > starsRef.current) {
        starsRef.current = passed;
        setStars(passed);
        const label = milestoneLabel(STREAK_MILESTONES[passed - 1] ?? 0);
        setToast(`${STREAK_PRAISE[Math.min(passed - 1, STREAK_PRAISE.length - 1)]} ${label} calm!`);
        clearTimeout(toastTimerRef.current);
        toastTimerRef.current = setTimeout(() => setToast(null), 2800);
      }
    }, 250);
    return () => clearInterval(id);
  }, []);

  useEffect(
    () => () => {
      clearTimeout(toastTimerRef.current);
      clearTimeout(brokeTimerRef.current);
    },
    [],
  );

  const [attempt, setAttempt] = useState(0);
  const status = useMicLevel(
    true,
    (level) => {
      const root = rootRef.current;
      if (root) root.style.setProperty('--level', level.toFixed(3));
      const next = levelToZone(level);
      if (next !== zoneRef.current) {
        zoneRef.current = next;
        setZone(next);
      }
    },
    sensitivityRef,
    attempt,
  );

  // Esc closes.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const meta = ZONE_META[zone];

  return createPortal(
    <div
      ref={rootRef}
      className={`${styles.overlay} ${styles[`zone_${zone}`] ?? ''}`}
      style={{ ['--level' as string]: '0' }}
      data-testid="noise-meter"
      data-zone={zone}
      role="dialog"
      aria-label="Classroom noise meter"
    >
      <div className={styles.intense} aria-hidden="true" />
      <div className={styles.vignette} aria-hidden="true" />

      <header className={styles.topbar}>
        <span className={styles.title}>Noise meter</span>
        <button
          type="button"
          className={styles.close}
          onClick={onClose}
          data-testid="noise-meter-close"
          aria-label="Close noise meter (Esc)"
        >
          Close ✕
        </button>
      </header>

      {status === 'running' ? (
        <div className={styles.goalBar}>
          {streakOn ? (
            <div
              className={`${styles.streak} ${broke ? styles.streakBroke : ''}`}
              data-testid="calm-streak"
            >
              <span
                className={`${styles.goalDot} ${zone !== 'loud' ? styles.goalDotOn : ''}`}
                aria-hidden="true"
              />
              <span className={styles.streakTime} data-testid="calm-streak-time">
                Calm {formatClock(streakMs)}
              </span>
              {stars > 0 ? (
                <span className={styles.streakStars} aria-hidden="true">
                  {'⭐'.repeat(Math.min(stars, 5))}
                  {stars > 5 ? ` ×${stars}` : ''}
                </span>
              ) : null}
              {bestMs > 2000 ? (
                <span className={styles.streakBest}>Best {formatClock(bestMs)}</span>
              ) : null}
              <button
                type="button"
                className={styles.goalStop}
                onClick={resetStreak}
                aria-label="Reset streak"
                data-testid="calm-streak-reset"
                title="Reset"
              >
                ↺
              </button>
              <button
                type="button"
                className={styles.goalStop}
                onClick={stopStreak}
                aria-label="Stop calm streak"
              >
                ✕
              </button>

              {toast ? (
                <span className={styles.streakToast} data-testid="calm-streak-toast">
                  <Confetti key={stars} />
                  {toast}
                </span>
              ) : null}
              {broke ? (
                <span className={styles.streakBrokeMsg}>Shh! Someone got loud — starting over</span>
              ) : null}
            </div>
          ) : (
            <button
              type="button"
              className={styles.goalToggle}
              onClick={startStreak}
              data-testid="calm-streak-toggle"
            >
              ⏱️ Calm streak
            </button>
          )}
        </div>
      ) : null}

      {status === 'running' ? (
        <div className={styles.stage}>
          <div className={styles.pulse} aria-hidden="true" />
          <div className={styles.meter} aria-hidden="true">
            <div className={styles.track}>
              <div className={styles.fill} />
              <div className={styles.needle} />
            </div>
            <div className={styles.zonesAxis}>
              <span>🤫</span>
              <span>🙂</span>
              <span>🔊</span>
            </div>
          </div>

          <div className={styles.readout} aria-live="polite">
            <span className={styles.emoji}>{meta.emoji}</span>
            <span className={styles.zoneLabel} data-testid="noise-meter-zone">
              {meta.label}
            </span>
            <span className={styles.blurb}>{meta.blurb}</span>
          </div>
        </div>
      ) : (
        <div className={styles.notice} data-testid="noise-meter-notice">
          {status === 'requesting' ? (
            <>
              <span className={styles.noticeEmoji}>🎤</span>
              <p>Allow microphone access to start the noise meter…</p>
            </>
          ) : status === 'denied' ? (
            <>
              <span className={styles.noticeEmoji}>🎤</span>
              <p>
                The noise meter couldn&rsquo;t reach your microphone. Allow mic access for this site
                (and for your browser in your computer&rsquo;s settings), then try again.
              </p>
              <button
                type="button"
                className={styles.noticeRetry}
                onClick={() => setAttempt((a) => a + 1)}
                data-testid="noise-meter-retry"
              >
                Try again
              </button>
            </>
          ) : (
            <>
              <span className={styles.noticeEmoji}>🔇</span>
              <p>
                This device doesn&rsquo;t support microphone input, so the noise meter can&rsquo;t
                run.
              </p>
            </>
          )}
        </div>
      )}

      <div className={styles.calibrateBar}>
        {calibrating ? (
          <div className={styles.calibrate} data-testid="noise-meter-calibrate">
            <span className={styles.calHint}>
              Make the volume you want flagged, then slide until the meter turns red.
            </span>
            <div className={styles.calRow}>
              <span className={styles.calEnd}>Calmer</span>
              <input
                type="range"
                min={MIN_SENSITIVITY}
                max={MAX_SENSITIVITY}
                step={0.05}
                value={sensitivity}
                onChange={(e) => updateSensitivity(Number.parseFloat(e.target.value))}
                className={styles.calSlider}
                aria-label="Noise meter sensitivity"
                data-testid="noise-meter-sensitivity"
              />
              <span className={styles.calEnd}>Stricter</span>
              <button
                type="button"
                className={styles.calDone}
                onClick={() => setCalibrating(false)}
                data-testid="noise-meter-calibrate-done"
              >
                Done
              </button>
            </div>
          </div>
        ) : (
          <button
            type="button"
            className={styles.calToggle}
            onClick={() => setCalibrating(true)}
            data-testid="noise-meter-calibrate-toggle"
          >
            ⚙ Calibrate
          </button>
        )}
      </div>
    </div>,
    document.body,
  );
}

// Calm-streak milestones (seconds) and escalating praise. Each milestone passed
// adds a star and pops a celebratory toast — the longer the streak, the more.
const STREAK_MILESTONES = [15, 30, 60, 120, 300, 600, 900, 1800];
const STREAK_PRAISE = [
  'Nice!',
  'Great!',
  'Awesome!',
  'Amazing!',
  'Incredible!',
  'Superstar!',
  'Legendary!',
  'Unstoppable!',
];

/** ms → "m:ss" for the running calm tally. */
function formatClock(ms: number): string {
  const total = Math.max(0, Math.round(ms / 1000));
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}

/** seconds → a friendly milestone label ("30 sec", "2 min"). */
function milestoneLabel(sec: number): string {
  if (sec < 60) return `${sec} sec`;
  const m = Math.round(sec / 60);
  return `${m} min`;
}

/** A short radial burst of emoji, remounted per milestone (key) to replay. */
function Confetti() {
  const pieces = ['🎉', '✨', '⭐', '🎊', '🌟', '💫'];
  const n = 12;
  return (
    <span className={styles.confetti} aria-hidden="true">
      {Array.from({ length: n }).map((_, i) => (
        <span
          key={i}
          className={styles.confettiPiece}
          style={{ ['--i' as string]: String(i), ['--n' as string]: String(n) }}
        >
          {pieces[i % pieces.length]}
        </span>
      ))}
    </span>
  );
}
